export const SITE_URL = "https://shopify-freelancer.net";
export const SITE_NAME = "Shopify Freelancer, Conversion Optimierung";

export type Faq = { q: string; a: string };

/**
 * Strukturierte Daten für Ratgeber-Artikel: FAQPage, Article und Breadcrumbs.
 * Klare Entitäten helfen klassischen Suchmaschinen und LLM-Suchen
 * (ChatGPT Search, Claude, Gemini, Perplexity) beim Zitieren.
 */
export function articleSchemas({
  title,
  description,
  path,
  faqs,
  section = "Shopify Conversion Optimierung",
  updated,
  breadcrumb = { name: "Ratgeber", path: "/ratgeber" },
}: {
  title: string;
  description: string;
  path: string;
  faqs: Faq[];
  section?: string;
  /** ISO-Datum der letzten inhaltlichen Aktualisierung. */
  updated?: string;
  breadcrumb?: { name: string; path: string };
}) {
  const url = `${SITE_URL}${path}`;

  return [
    {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Article",
        headline: title,
        description,
        inLanguage: "de-DE",
        articleSection: section,
        mainEntityOfPage: { "@type": "WebPage", "@id": url },
        url,
        author: { "@type": "Person", name: "Shopify Freelancer", url: SITE_URL },
        publisher: {
          "@type": "Organization",
          name: SITE_NAME,
          url: SITE_URL,
        },
        about: [
          { "@type": "Thing", name: "Shopify" },
          { "@type": "Thing", name: "Conversion-Rate-Optimierung" },
          { "@type": "Thing", name: "E-Commerce" },
        ],
        ...(updated ? { dateModified: updated } : {}),
        speakable: {
          "@type": "SpeakableSpecification",
          cssSelector: ["h1", "[data-answer]"],
        },
      }),
    },
    {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Start", item: `${SITE_URL}/` },
          {
            "@type": "ListItem",
            position: 2,
            name: breadcrumb.name,
            item: `${SITE_URL}${breadcrumb.path}`,
          },
          { "@type": "ListItem", position: 3, name: title, item: url },
        ],
      }),
    },
    {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }),
    },
  ];
}

/**
 * Zusätzliche Schemas für Antwortseiten (/fragen/*): QAPage mit einer
 * klaren Kurzantwort, die KI-Suchen direkt zitieren können.
 */
export function qaSchema({
  question,
  answer,
  path,
  updated,
}: {
  question: string;
  answer: string;
  path: string;
  updated?: string;
}) {
  const url = `${SITE_URL}${path}`;
  return {
    type: "application/ld+json",
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "QAPage",
      inLanguage: "de-DE",
      mainEntity: {
        "@type": "Question",
        name: question,
        text: question,
        answerCount: 1,
        ...(updated ? { dateModified: updated } : {}),
        acceptedAnswer: {
          "@type": "Answer",
          text: answer,
          url,
        },
      },
    }),
  };
}

/** ItemList für Übersichtsseiten, damit Crawler die Sammlung als Einheit lesen. */
export function itemListSchema(items: { name: string; path: string }[], name: string) {
  return {
    type: "application/ld+json",
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ItemList",
      name,
      itemListElement: items.map((it, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: it.name,
        url: `${SITE_URL}${it.path}`,
      })),
    }),
  };
}
